import { useMutation, useQueryClient } from "@tanstack/react-query";

import { TixologiTicketType } from "@punchup/data/legacy/tixologi";
import { VenuePagesRow } from "@punchup/venues/data/venue_pages";
import axios from "axios";
import { logException } from "@punchup/sentry";
import { useTixologi } from "@punchup/data/legacy/hooks";

export type BulkUpdateTicketTypeItem = {
  ticketTypeId: number;
  data: Partial<TixologiTicketType>;
};

export type BulkUpdateTicketTypesResponse = {
  summary: { total: number; succeeded: number; failed: number };
  errors: { ticketTypeId: number; error: string }[];
};

interface UseBulkUpdateTicketTypesReturn {
  mutateAsync: (
    items: BulkUpdateTicketTypeItem[]
  ) => Promise<BulkUpdateTicketTypesResponse>;
  isPending: boolean;
}

/**
 * Bulk updates Tixologi ticket types in parallel via per-id PUTs to
 * `/ticket_types/{id}`. Partial failures are returned in the response rather
 * than thrown – callers own the toast/UX. Cache invalidation runs once at the
 * end if at least one update succeeded.
 */
export function useBulkUpdateTicketTypes({
  venuePage,
}: {
  venuePage: VenuePagesRow;
}): UseBulkUpdateTicketTypesReturn {
  const { tixologiData } = useTixologi({
    id: venuePage.id,
    isComedian: false,
  });
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (
      items: BulkUpdateTicketTypeItem[]
    ): Promise<BulkUpdateTicketTypesResponse> => {
      // Reject early on a missing/expired token so callers can surface it
      // distinctly, instead of N "Bearer undefined" 401s counted as failures.
      if (!tixologiData?.token) {
        throw new Error(
          "Your Tixologi session has expired. Please refresh the page and try again."
        );
      }

      const results = await Promise.allSettled(
        items.map(({ ticketTypeId, data }) =>
          axios.put(
            process.env.NEXT_PUBLIC_TIXOLOGI_API_URL +
              `/ticket_types/${ticketTypeId}`,
            data,
            { headers: { Authorization: `Bearer ${tixologiData?.token}` } }
          )
        )
      );

      const errors = results.flatMap((result, idx) =>
        result.status === "rejected"
          ? [
              {
                ticketTypeId: items[idx].ticketTypeId,
                error:
                  result.reason instanceof Error
                    ? result.reason.message
                    : String(result.reason),
              },
            ]
          : []
      );

      if (errors.length > 0) {
        logException(
          new Error(
            `Bulk ticket type update: ${errors.length}/${items.length} updates failed`
          ),
          {
            tags: { tixologi: true },
            extra: { errors, venuePageId: venuePage.id },
          }
        );
      }

      return {
        summary: {
          total: items.length,
          succeeded: items.length - errors.length,
          failed: errors.length,
        },
        errors,
      };
    },
    onSuccess: (response) => {
      if (response.summary.succeeded > 0) {
        queryClient.invalidateQueries({
          queryKey: ["showsWithTixologi"],
          refetchType: "active",
        });
      }
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
}
